// THE ASTRYX THEME, AND WHY THE APP IS UNREADABLE WITHOUT ONE.
//
// E14-t1. Importing `@astryxdesign/core/astryx.css` is NOT enough to get colours,
// and the app had done exactly that and nothing else since it was written. Every
// colour token in the stylesheet is authored as `light-dark(<light>, <dark>)`:
//
//     :root { --color-accent: light-dark(#0064E0, #2694FE); }
//
// Vite compiles CSS with LightningCSS, which POLYFILLS that function into a pair
// of guarded custom properties, exactly one of which is meant to collapse:
//
//     --color-accent: var(--lightningcss-light,#0064e0) var(--lightningcss-dark,#2694fe)
//
// The guards are emitted only inside Astryx's StyleX theme classes, and this app
// does not compile StyleX — `<Theme>` renders with an EMPTY className — so
// NEITHER guard was ever defined, both fell back, and the property resolved to
// the two colours CONCATENATED: "#0064e0#2694fe". Not a colour. A primary button
// computed `background-color: rgba(0,0,0,0)` with white text, on a white card.
// It was not missing from the page; it was invisible on it. Fixed by raising
// `build.cssTarget` (vite.config.ts) so nothing polyfills `light-dark()`, and by
// mounting `<Theme>` at the root — which is what sets the colour scheme.
//
// ─────────────────────────────────────────────────────────────────────────────
//
// E15-t1 — DENSITY AND TYPE ARE OURS. COLOUR IS THE DASHBOARD'S.
//
// This file briefly carried the full palette of `web/design/forge-prototype.html`
// — warm greys, a violet accent, five state hues. That was REVERTED, on purpose,
// by the answer to Forge Design Language q2:
//
//     Keep Astryx's defaults. Use the theme of the dashboard we are generating.
//
// The reasoning is stronger than the palette was. This app's central job is to
// FRAME A RENDERED DASHBOARD: the shell surrounds a page the engine published,
// and that page carries its own stylesheet — docujoint's `src/render/tokens.ts`,
// which is Astryx's own token set transpiled out of `light-dark()`. Verified,
// not assumed: the dashboard's `--color-accent` is `#0064E0`, its
// `--color-background-body` is `#F1F4F7`, its `--color-success` is `#0D8626`.
// Stock Astryx.
//
// So a bespoke palette here would have made the chrome and the thing it frames
// two different products with a seam down the middle — the exact failure
// src/main.tsx's header already warns about ("importing the same token layer is
// what makes the shell and the thing it frames one product rather than two
// vocabularies"). A violet shell around a blue dashboard is worse than a blue
// shell around a blue dashboard, however much nicer the violet is alone.
//
// WHAT THIS COSTS, stated because it is a real loss and not a free win: the
// prototype held its accent OUTSIDE the semantic set so that branding could
// never wear a verdict's colour. Astryx's defaults do not offer that separation
// — it ships no `--color-info` at all, and `Badge variant="info"` paints solid
// `--color-accent`, so Astryx's "info" IS its brand blue. Since format.yaml
// spends `info` on DRIFT, drift and the brand are now the same family.
// Mitigated, not solved, by form rather than hue: the state chip carries a
// square dot and a word, so a drifted row is still legible as drift in
// greyscale, to a colour-blind reader, and next to a brand-blue button. If that
// mitigation ever stops being enough, the answer is a drift hue in THIS file
// and in the engine's tokens together — never in one of them alone.
//
// WHAT SURVIVES, because q1 answered "visual language first": the prototype's
// DENSITY and TYPE. Those are not colour and they do not fight the dashboard —
// the four-point scale, the 1.55 leading, the mono/sans split and the small
// radii are what make a row scannable, and they are kept below.
import { defineTheme } from '@astryxdesign/core/theme';

/**
 * The product's theme: Astryx's palette, this product's density.
 *
 * `tokens` carries no `--color-*` and no `--shadow-*` ON PURPOSE — every hue,
 * wash, overlay and shadow is Astryx's own, which is the dashboard's own. What
 * is overridden is the type scale, the leading and the radii.
 *
 * Astryx's GENERATORS are all declined, each for a stated reason:
 *   - `color: { accent }` derives a whole palette from a seed. There is nothing
 *     to derive: the palette is the default one, deliberately.
 *   - `radius: { base, multiplier }` is geometric — inner/element/container are
 *     base×1, ×2, ×3. The prototype's are 3px, 6px and 7px, which no base
 *     produces, so the radii are written out.
 *   - `typography.scale: { base, ratio }` is a geometric progression too, and
 *     the density ladder is a hand-picked 14 / 13.5 / 13 / 12.5 / 10.5 that no
 *     ratio hits. Supplying it would also emit component overrides for every
 *     Text type and Heading level that merely restate tokens the components
 *     already read — CSS with no effect.
 */
export const docujointTheme = defineTheme({
  name: 'docujoint',

  /**
   * MONO IS FOR MACHINE FACTS AND SANS IS FOR PROSE (E15-t2). The split is what
   * makes a row scannable without decoration, and it starts here. `heading` is
   * deliberately absent — Astryx falls it back to `body`, which is what the
   * prototype does.
   */
  typography: {
    // Figtree is the adopted design's face (Docujoint.dc.html loads it in its
    // helmet; web/index.html now loads it the same way). E15-t1's ruling — type
    // is OURS, colour is the dashboard's — is what makes this a legal move
    // where a palette would not be: the shell may set its face, and the
    // system-ui stack stays as the fallback for the moment the webfont hasn't
    // arrived.
    body: {
      family: 'Figtree',
      fallbacks: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    },
    code: {
      family: 'ui-monospace',
      fallbacks: '"SF Mono", "Cascadia Mono", "JetBrains Mono", Menlo, Consolas, monospace',
    },
  },

  tokens: {
    // ── radii ───────────────────────────────────────────────────────────────
    // A card is bordered rather than floated, so its corner does visible work:
    // 7px for a card, 6px for a control, 3px for a chip.
    '--radius-inner': '3px',
    '--radius-element': '6px',
    '--radius-container': '7px',
    '--radius-page': '10px',
    '--radius-chat': '10px',

    // ── the four-point density ladder ───────────────────────────────────────
    // 14 prose · 13.5 nav · 13 tables · 12.5 metadata · 10.5 chips and eyebrows.
    // A working surface, not a marketing page: the whole vocabulary sits inside
    // four points, and every named slot below is one of the prototype's sizes.
    '--font-size-4xs': '0.5rem', // 8
    '--font-size-3xs': '0.65625rem', // 10.5 — chips, eyebrow labels
    '--font-size-2xs': '0.78125rem', // 12.5 — metadata, machine facts
    '--font-size-xs': '0.8125rem', // 13 — tables
    '--font-size-sm': '0.84375rem', // 13.5 — navigation
    '--font-size-base': '0.875rem', // 14 — prose
    '--font-size-lg': '1rem', // 16
    '--font-size-xl': '1.125rem', // 18
    '--font-size-2xl': '1.3125rem', // 21 — the prototype's h1
    '--font-size-3xl': '1.5rem', // 24
    '--font-size-4xl': '1.75rem', // 28
    '--font-size-5xl': '2rem', // 32
    '--font-weight-medium': '560',

    // ── leading and the per-role sizes ──────────────────────────────────────
    // 1.55 throughout: the prototype sets it once on `body` and never varies it.
    '--text-body-size': 'var(--font-size-base)',
    '--text-body-leading': '1.55',
    '--text-label-size': 'var(--font-size-sm)',
    '--text-label-leading': '1.55',
    '--text-supporting-size': 'var(--font-size-2xs)',
    '--text-supporting-leading': '1.55',
    // `code` is the mono face, and mono is where the machine facts live — ids,
    // counts, commits, branch names, byte counts — so it takes the metadata size.
    '--text-code-size': 'var(--font-size-2xs)',
    '--text-code-leading': '1.55',
    // A page title is `Heading level={1} type="display-3"`, and the prototype's
    // h1 is 21px at 650 — nowhere near a marketing display size.
    '--text-display-3-size': 'var(--font-size-2xl)',
    '--text-display-2-size': 'var(--font-size-3xl)',
    '--text-display-1-size': 'var(--font-size-4xl)',
    '--text-display-3-weight': '650',
    '--text-display-2-weight': '650',
    '--text-display-1-weight': '650',
    '--text-heading-1-weight': '650',
    '--text-heading-2-weight': '640',
    '--text-heading-3-weight': '640',
    '--text-heading-2-size': 'var(--font-size-xs)',
    '--text-heading-2-leading': '1.55',
    '--text-heading-3-size': 'var(--font-size-xs)',
    '--text-heading-3-leading': '1.55',
  },
});
